import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { updateEmail, updatePassword, updateProfile, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { checkSubscription } from '../subscriptionUtils';
import logo from '../assets/logo.png';
import './Settings.css';

const Settings = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [surname, setSurname] = useState('');
    const [company, setCompany] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showReauth, setShowReauth] = useState(false);
    const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
    const [isProUser, setIsProUser] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            const user = auth.currentUser;
            if (user) {
                try {
                    setLoading(true);
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        setFirstName(data.name || '');
                        setSurname(data.surname || '');
                        setEmail(data.email || user.email || '');
                        setCompany(data.company || '');
                    } else {
                        setEmail(user.email || '');
                        const displayName = user.displayName || '';
                        const nameParts = displayName.split(' ');
                        setFirstName(nameParts[0] || '');
                        setSurname(nameParts[1] || '');
                        setCompany(localStorage.getItem('company') || '');
                    }

                    const { isAuthenticated, planLevel } = await checkSubscription();
                    if (!isAuthenticated) {
                        navigate('/login');
                        return;
                    }
                    setIsProUser(planLevel === 'Pro');
                } catch (err) {
                    setError('Failed to load user data.');
                    console.error('Fetch user data error:', err);
                } finally {
                    setLoading(false);
                }
            } else {
                navigate('/login');
            }
        };
        fetchUserData();
    }, [navigate]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const handleReauthenticate = async () => {
        try {
            const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
            await reauthenticateWithCredential(auth.currentUser, credential);
            setShowReauth(false);
            setCurrentPassword('');
            return true;
        } catch (err) {
            setError('Re-authentication failed. Please check your current password.');
            return false;
        }
    };

    const handleSave = async () => {
        setMessage('');
        setError('');
        setLoading(true);

        if (!firstName.trim() || !surname.trim()) {
            setError('Name and Surname are required.');
            setLoading(false);
            return;
        }
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email.');
            setLoading(false);
            return;
        }

        try {
            const user = auth.currentUser;
            let needsReauth = false;

            if (email !== user.email || password) {
                needsReauth = true;
            }

            if (needsReauth) {
                setShowReauth(true);
                setLoading(false);
                return;
            }

            const userData = {
                name: firstName,
                surname: surname,
                displayName: `${firstName} ${surname}`,
                email: email,
                company: company || null,
                updatedAt: new Date().toISOString()
            };
            await updateDoc(doc(db, 'users', user.uid), userData);

            await updateProfile(user, { displayName: `${firstName} ${surname}` });

            localStorage.setItem('company', company);
            localStorage.setItem('user', JSON.stringify({ ...userData, uid: user.uid }));

            setMessage('Settings updated successfully!');
        } catch (err) {
            setError(`Error: ${err.message}`);
            console.error('Save settings error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleReauthSubmit = async () => {
        setLoading(true);
        setError('');
        const reauthSuccess = await handleReauthenticate();
        if (reauthSuccess) {
            try {
                const user = auth.currentUser;

                if (email !== user.email) {
                    await updateEmail(user, email);
                    await updateDoc(doc(db, 'users', user.uid), { email });
                }

                if (password) {
                    await updatePassword(user, password);
                }

                const userData = {
                    name: firstName,
                    surname: surname,
                    displayName: `${firstName} ${surname}`,
                    email: email,
                    company: company || null,
                    updatedAt: new Date().toISOString()
                };
                await updateDoc(doc(db, 'users', user.uid), userData);

                await updateProfile(user, { displayName: `${firstName} ${surname}` });

                localStorage.setItem('company', company);
                localStorage.setItem('user', JSON.stringify({ ...userData, uid: user.uid }));

                setMessage('Settings updated successfully!');
                setPassword('');
            } catch (err) {
                setError(`Error: ${err.message}`);
                console.error('Re-auth save error:', err);
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    };

    useEffect(() => {
        const isDark = localStorage.getItem('darkMode') === 'true';
        setDarkMode(isDark);
        document.body.classList.toggle('dark-mode', isDark);

        const onStorage = (e) => {
            if (e.key === 'darkMode') {
                const next = localStorage.getItem('darkMode') === 'true';
                setDarkMode(next);
                document.body.classList.toggle('dark-mode', next);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const toggleDarkMode = () => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        localStorage.setItem('darkMode', newMode.toString());
        document.body.classList.toggle('dark-mode', newMode);
    };

    const handleManageSubscription = () => {
        navigate('/subscription/manage');
    };

    return (
        <div className="settings-page">
            <div className="settings-container">
                <div className="settings-logo-wrap">
                    <img src={logo} alt="Customs Pro AI Logo" className="settings-logo" />
                </div>
                
                <nav className="nav">
                    <Link to="/" className="nav-item">Home</Link>
                    <Link to="/app" className="nav-item">Classify Goods</Link>
                    <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                    <div
                        className={`nav-item pro-dropdown ${!isProUser ? 'disabled' : ''} ${isProDropdownOpen ? 'open' : ''}`}
                        title={!isProUser ? 'Pro level users only, please upgrade your subscription' : ''}
                        onClick={() => isProUser && setIsProDropdownOpen(!isProDropdownOpen)}
                        role="button"
                        aria-expanded={isProDropdownOpen}
                        aria-controls="pro-dropdown-menu"
                    >
                        <span>Pro</span>
                        <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                            <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                            <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                            <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                            <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                            <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                            <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
                            <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                            <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                            <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                            <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                        </div>
                    </div>
                    <span className="nav-item active">Settings</span>
                    <span
                        className="nav-item logout"
                        onClick={() => auth.signOut().then(() => navigate('/'))}
                        role="button"
                        tabIndex={0}
                    >
                        Logout
                    </span>
                </nav>
                <h2 className="settings-title">Settings</h2>
                {loading && <p>Loading...</p>}
                {showReauth ? (
                    <div className="reauth-form">
                        <h3>Please Re-authenticate</h3>
                        <p>Enter your current password to update email or password.</p>
                        <div className="form-group">
                            <label>Current Password:</label>
                            <input
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="settings-input"
                            />
                        </div>
                        <button className="save-btn" onClick={handleReauthSubmit} disabled={loading || !currentPassword}>
                            Submit
                        </button>
                        <button className="cancel-btn" onClick={() => setShowReauth(false)} disabled={loading}>
                            Cancel
                        </button>
                        {error && <p className="error">{error}</p>}
                    </div>
                ) : (
                    <div className="settings-form">
                        <div className="form-group name-group">
                            <div className="name-row">
                                <div className="name-field">
                                    <label>Name:</label>
                                    <input
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className="settings-input"
                                    />
                                </div>
                                <div className="name-field">
                                    <label>Surname:</label>
                                    <input
                                        type="text"
                                        value={surname}
                                        onChange={(e) => setSurname(e.target.value)}
                                        className="settings-input"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Email:</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="settings-input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Company (Optional):</label>
                            <input
                                type="text"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                className="settings-input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Password (leave blank to keep current):</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="settings-input"
                            />
                        </div>
                        <div className="form-group terms-display">
                            <label>Terms of Service:</label>
                            <p>Accepted at registration</p>
                        </div>
                        <div className="form-group toggle-group">
                            <label htmlFor="theme-toggle">Appearance</label>
                            <div className="toggle-row">
                                <span className="toggle-mode-label" id="theme-toggle-label">
                                    {darkMode ? 'Dark' : 'Light'}
                                </span>
                                <button
                                    type="button"
                                    id="theme-toggle"
                                    className={`toggle-switch ${darkMode ? 'on' : ''}`}
                                    onClick={toggleDarkMode}
                                    role="switch"
                                    aria-checked={darkMode}
                                    aria-labelledby="theme-toggle-label"
                                    aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                                >
                                    <span className="switch-slider" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                        <button className="save-btn" onClick={handleSave} disabled={loading}>
                            Save Changes
                        </button>
                        <button
                            className="manage-sub-btn"
                            onClick={handleManageSubscription}
                            disabled={loading}
                        >
                            Manage Subscription
                        </button>
                        {message && <p className="message">{message}</p>}
                        {error && <p className="error">{error}</p>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settings;